import React, {
  useContext,
  useEffect,
  useReducer,
  useRef,
  useState,
} from "react";
import toastError from "../../errors/toastError";
import { getBackendUrl } from "../../config";
import { MdOutlineForum } from "react-icons/md";
import api from "../../services/api";
import { isArray } from "lodash";
import { useDate } from "../../hooks/useDate";
import { AuthContext } from "../../context/Auth/AuthContext";

import notifySound from "../../assets/chat_notify.mp3";
import useSound from "use-sound";
import { i18n } from "../../translate/i18n";
import { useHistory } from "react-router-dom";
import { format } from "date-fns";
import { socketConnection } from "../../services/socket";

const reducer = (state, action) => {
  if (action.type === "LOAD_CHATS") {
    const chats = action.payload;
    const newChats = [];

    if (isArray(chats)) {
      chats.forEach((chat) => {
        const chatIndex = state.findIndex((u) => u.id === chat.id);
        if (chatIndex !== -1) {
          state[chatIndex] = chat;
        } else {
          newChats.push(chat);
        }
      });
    }

    return [...state, ...newChats];
  }

  if (action.type === "UPDATE_CHATS") {
    const chat = action.payload;
    const chatIndex = state.findIndex((u) => u.id === chat.id);

    if (chatIndex !== -1) {
      state[chatIndex] = chat;
      return [...state];
    } else {
      return [chat, ...state];
    }
  }

  if (action.type === "DELETE_CHAT") {
    const chatId = action.payload;

    const chatIndex = state.findIndex((u) => u.id === chatId);
    if (chatIndex !== -1) {
      state.splice(chatIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }

  if (action.type === "CHANGE_CHAT") {
    const changedChats = state.map((chat) => {
      if (chat.id === action.payload.chat.id) {
        return action.payload.chat;
      }
      return chat;
    });
    return changedChats;
  }
};

export default function ChatPopover(volume) {
  //   const socketManager = useContext(SocketContext);
  const { user: loggedUser, socket } = useContext(AuthContext);
  const history = useHistory();

  const historyRef = useRef(history);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [searchParam] = useState("");
  const [chats, dispatch] = useReducer(reducer, []);
  const [invisible, setInvisible] = useState(true);
  const { datetimeToClient } = useDate();
  const [play] = useSound(notifySound, volume);
  const soundAlertRef = useRef();

  useEffect(() => {
    soundAlertRef.current = play;

    if (!("Notification" in window)) {
      console.log("This browser doesn't support notifications");
    } else {
      Notification.requestPermission();
    }
  }, [play]);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      fetchChats();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParam, pageNumber]);

  useEffect(() => {
    if (loggedUser.id) {
      const companyId = loggedUser.companyId;
      const socket = socketConnection({ companyId, userId: loggedUser.id });

      const onCompanyChatPopover = (data) => {
        if (data.action === "new-message") {
          dispatch({ type: "CHANGE_CHAT", payload: data });
          console.log(data);
          if (
            data.newMessage.senderId !== loggedUser.id &&
            data.chat.users.find((user) => user.userId === loggedUser.id)
          ) {
            soundAlertRef.current();

            // Check if notifications are permitted
            if (Notification.permission === "granted") {
              const notification = new Notification(
                "Chat interno nº " + data.newMessage.chatId,
                {
                  body: `${data.newMessage.sender.name}: ${
                    data.newMessage.message
                  } - ${format(new Date(), "HH:mm")}`,
                  icon: data.newMessage.sender.profileImage
                    ? `${getBackendUrl()}/public/company${data.newMessage.sender.companyId}/user/${data.newMessage.sender.profileImage}`
                    : undefined,
                }
              );

              notification.onclick = (e) => {
                e.preventDefault();
                window.focus();
                historyRef.current.push(`/chats/${data.newMessage.chatId}`);
              };
            }
          }
        }
        if (data.action === "update") {
          dispatch({ type: "CHANGE_CHAT", payload: data });
        }
      };

      socket.on(`company-${companyId}-chat`, onCompanyChatPopover);

      return () => {
        socket.off(`company-${companyId}-chat`, onCompanyChatPopover);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedUser.id, loggedUser.companyId]);

  useEffect(() => {
    let unreadsCount = 0;
    if (chats.length > 0) {
      for (let chat of chats) {
        for (let chatUser of chat.users) {
          if (chatUser.userId === loggedUser.id) {
            unreadsCount += chatUser.unreads;
          }
        }
      }
    }
    if (unreadsCount > 0) {
      setInvisible(false);
    } else {
      setInvisible(true);
    }
  }, [chats, loggedUser.id]);

  const fetchChats = async () => {
    try {
      const { data } = await api.get("/chats/", {
        params: { searchParam, pageNumber },
      });
      dispatch({ type: "LOAD_CHATS", payload: data.records });
      setHasMore(data.hasMore);
      setLoading(false);
    } catch (err) {
      toastError(err);
    }
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  const handleClick = () => {
    setOpen(prevState => !prevState);
    setInvisible(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const goToMessages = (chat) => {
    handleClose();
    history.push(`/chats/${chat.uuid}`);
  };

  useEffect(() => {
    if (!open) return undefined;

    const handleOutsideClick = event => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        handleClose();
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [open]);

  const id = open ? "simple-popover" : undefined;

  return (
    <div className="relative h-full w-full" ref={dropdownRef}>
      <button
        type="button"
        aria-describedby={id}
        aria-label={i18n.t("mainDrawer.listItems.chats", "Chat interno")}
        onClick={handleClick}
        className="flex h-full w-full cursor-pointer items-center justify-center rounded-[10px] border-0 bg-transparent p-0 text-inherit outline-none transition-colors hover:bg-farmazon-hover"
      >
        <span className="relative inline-flex">
          <MdOutlineForum size={18} />
          {!invisible && (
            <span className="absolute -right-1 -top-1 h-[9px] w-[9px] rounded-full border-2 border-farmazon-surface bg-red-500" />
          )}
        </span>
      </button>
      {open && (
        <div
          id={id}
          onScroll={handleScroll}
          className="absolute right-0 top-full z-[1300] mt-2 max-h-[300px] w-[340px] overflow-y-auto rounded-xl border border-farmazon-border bg-farmazon-surface p-2 shadow-xl"
        >
          {isArray(chats) && chats.length > 0 ? (
            <div className="flex flex-col gap-1">
              {chats.map((item) => {
                const title = typeof item.lastMessage === "string"
                  ? item.title || "Chat"
                  : item.lastMessage?.sender?.name || item.title || "Chat";
                const message = typeof item.lastMessage === "string"
                  ? item.lastMessage
                  : item.lastMessage?.message || "Nenhuma mensagem";

                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => goToMessages(item)}
                    className="w-full cursor-pointer rounded-lg border-0 bg-farmazon-surface px-3 py-2 text-left transition-colors hover:bg-farmazon-hover"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="truncate text-sm font-bold text-farmazon-text">
                          {title}
                        </div>
                        <div className="mt-0.5 truncate text-xs text-farmazon-text-muted">
                          {message}
                        </div>
                      </div>
                      <span className="shrink-0 text-[11px] text-farmazon-text-faint">
                        {datetimeToClient(item.updatedAt)}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="px-3 py-4 text-sm font-medium text-farmazon-text-muted">
              {i18n.t("mainDrawer.appBar.notRegister")}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
